import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useGetCueCardsQuery, useGetAutoCuecardsQuery } from "../api/apiSlice";

export default function CueCards() {

  const meetingId = useSelector((state) => state.meeting.meetingId);
  const organizerId = useSelector((state) => state.meeting.organizerId);
  const [transcriptInput, setTranscriptInput] = useState("");
  const [expandedCardId, setExpandedCardId] = useState(null);

  const { data: cueCardsData = [], error, isLoading } = useGetCueCardsQuery({ meetingId, organizerId }, {
        skip: !meetingId || !organizerId,
  });

  // Cards auto-surfaced from the meeting transcript (Microsoft Graph). The
  // transcript is only available AFTER the meeting ends, so this is empty during
  // the call - the manual simulator below covers the live case.
  const { data: autoCards = [] } = useGetAutoCuecardsQuery(
    { meetingId, organizerId },
    { skip: !meetingId || !organizerId, pollingInterval: 30000 }
  );

  const visibleCueCards = useMemo(() => {
    if (transcriptInput.trim().length === 0) {
      return cueCardsData;
    }
    const inputWords = transcriptInput
      .toLowerCase()
      .split(/[\s,.#]+/)
      .filter((w) => w.length > 0);

    if (inputWords.length === 0) {
      return cueCardsData;
    }

    return cueCardsData.filter((card) => {
      const tags = (card.tags || [])
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
      
      return inputWords.some((word) => {
        // Tag fuzzy match: tag contains typed word, or typed word contains tag
        const tagMatch = tags.some((tag) => tag.includes(word) || (tag && word.includes(tag)));
        if (tagMatch) return true;

        // Safely search question and answer texts, preventing TypeError crashes if undefined/null
        const questionMatch = (card.question || "").toLowerCase().includes(word);
        const answerMatch = (card.answer || "").toLowerCase().includes(word);
        return questionMatch || answerMatch;
      });
    });
  }, [cueCardsData, transcriptInput]);


  const toggleCard = (id) => {
    setExpandedCardId(expandedCardId === id ? null : id);
  };

  const isTagMatched = (tag) => {
    if (!transcriptInput.trim()) return false;
    const inputWords = transcriptInput
      .toLowerCase()
      .split(/[\s,.#]+/)
      .filter((w) => w.length > 0);
    const lowerTag = tag.trim().toLowerCase();
    if (!lowerTag) return false;
    return inputWords.some((word) => lowerTag.includes(word) || word.includes(lowerTag));
  };

  const handleClear = () => {
    setTranscriptInput("");
  };

  const handleTagClick = (tag, e) => {
    e.stopPropagation(); // Avoid expanding card when clicking tag
    setTranscriptInput(tag);
  };

  return (
    <section className="widget-card">
      <h3 className="widget-title">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        Cue Cards suggestions
      </h3>

      {autoCards.length > 0 && (
        <div style={{ marginBottom: "16px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              marginBottom: "8px",
              color: "var(--success)",
              fontWeight: 600,
              fontSize: "0.85rem",
            }}
          >
            <span
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                background: "var(--success)",
                display: "inline-block",
              }}
            />
            Surfaced from this meeting's transcript
          </div>
          <ul className="cuecards-list">
            {autoCards.map((card) => {
              const isExpanded = expandedCardId === card.id;
              return (
                <li
                  key={`auto-${card.id}`}
                  onClick={() => toggleCard(card.id)}
                  className={`cuecard-item ${isExpanded ? "expanded" : ""}`}
                >
                  <div className="cuecard-header">
                    <span className="cuecard-question">{card.question}</span>
                    <span className="cuecard-toggle-icon">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </span>
                  </div>
                  {isExpanded && (
                    <div className="cuecard-body">
                      <div style={{ color: "var(--text-primary)" }}>
                        {card.answer}
                      </div>
                    </div>
                  )}
                  {card.tags && card.tags.length > 0 && (
                    <div className="cuecard-tags">
                      {card.tags.map((tag) => (
                        <span key={tag} className="tag-chip active-match">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="simulation-info">
        <div style={{ display: "flex", gap: "6px", alignItems: "flex-start" }}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            style={{ marginTop: "2px", flexShrink: 0 }}
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <div>
            <strong>Interactive Transcript Simulator:</strong> Type spoken words in the input below to trigger contextual suggestion cards (e.g. key words in tag chips). Click tag chips to auto-fill them.
          </div>
        </div>
      </div>

      <div style={{ position: "relative", marginBottom: "16px" }}>
        <input
          type="text"
          value={transcriptInput}
          onChange={(e) => setTranscriptInput(e.target.value)}
          placeholder="Type transcript triggers (e.g. pricing, competition, schedule)..."
          className="custom-input"
          style={{ paddingRight: "36px" }}
        />
        {transcriptInput && (
          <button
            onClick={handleClear}
            style={{
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "transparent",
              border: "none",
              color: "var(--text-muted)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "4px",
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>

      {isLoading && (
        <div style={{ padding: "16px 0", display: "flex", gap: "8px", flexDirection: "column" }}>
          <div style={{ height: "45px", background: "var(--skeleton-bg)", borderRadius: "8px" }} />
          <div style={{ height: "45px", background: "var(--skeleton-bg)", borderRadius: "8px" }} />
        </div>
      )}

      {error && (
        <div style={{ color: "var(--danger)", fontSize: "0.85rem", padding: "8px 0" }}>
          Failed to load cue cards suggestions.
        </div>
      )}

      {!isLoading && !error && (
        <ul className="cuecards-list">
          {visibleCueCards.length === 0 && (
            <div className="no-cards-placeholder">
              
              No matching cue cards. Try typing other keywords or clearing the simulation box.
            </div>
          )}
          
          {visibleCueCards.map((card) => {
            const isExpanded = expandedCardId === card.id;
            return (
              <li
                key={card.id}
                onClick={() => toggleCard(card.id)}
                className={`cuecard-item ${isExpanded ? "expanded" : ""}`}
              >
                <div className="cuecard-header">
                  <span className="cuecard-question">{card.question}</span>
                  <span className="cuecard-toggle-icon">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </div>

                {isExpanded && (
                  <div className="cuecard-body">
                    <div style={{ color: "var(--text-primary)" }}>{card.answer}</div>
                  </div>
                )}

                {card.tags && card.tags.length > 0 && (
                  <div className="cuecard-tags">
                    {card.tags.map((tag) => (
                      <span
                        key={tag}
                        onClick={(e) => handleTagClick(tag, e)}
                        className={`tag-chip ${isTagMatched(tag) ? "active-match" : ""}`}
                        style={{ cursor: "pointer" }}
                        title="Click to simulate this keyword trigger"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
